import React, { useEffect, useState, useRef } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import YouTube from "react-youtube";
import sl17Image from 'images/surflife/sl7.jpg';
import { useNavigate } from "react-router-dom";
import handleNavClick from "../../helpers/useSmoothScroll";
import useScrollToSavedSection from "../../helpers/useScrollToSavedSection";
import LanguageSelector, { useLanguage, pageMappings } from "../../helpers/languageSelector";
import client from 'sanityClient';
import Header, { NavLink, NavLinks, LogoLink, NavToggle, DesktopNavLinks } from "../headers/light.js";
import { urlFor } from 'sanityImageUrl';

const StyledHeader = styled(Header)`
  ${tw`pt-8 pb-2 max-w-none`}
  ${DesktopNavLinks} ${NavLink}, ${LogoLink} {
    ${tw`text-gray-700 hover:border-gray-900 hover:text-gray-900`}
  }
  ${NavToggle}.closed {
    ${tw`text-gray-700 bg-gray-100 hover:text-primary-900`}
  }
  ${NavToggle} {
    ${tw`fixed bg-transparent rounded-md p-2 right-[1rem] z-30 md:static`}
    top: calc(1.5rem + var(--info-banner-height, 0px));
  }
`;

const Container = styled.div`
  ${tw`relative -mx-8 -mt-12 z-20 bg-white bg-center bg-cover`}
  background-position: top center;
  background-size: cover;

  &::before, &::after {
    ${tw`absolute inset-x-0 bg-white`}
    content: '';
    z-index: 0;
  }

  &::before {
    ${tw`h-24 top-0`}
  }
`;

const OpacityOverlay = tw.div`z-10 absolute inset-0 bg-primary-300 opacity-5`;

const HeroContainer = tw.div`z-20 relative px-4 sm:px-8 max-w-screen-xl mx-auto`;

const PrimaryAction = styled.a`
  ${tw`px-6 cursor-pointer py-2 text-sm sm:text-base sm:px-6 sm:py-2 bg-primary-600 text-gray-300 font-bold shadow transition duration-300 hocus:bg-primary-700 hocus:text-gray-100 focus:shadow-outline`}
  ${tw`md:fixed md:right-[1rem] md:z-30`}
  @media (min-width: 768px) {
    top: calc(1rem + var(--info-banner-height, 0px));
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  background: transparent; /* Ensure no background color shows */
  padding-bottom: 56.25%; /* 16:9 */
  height: 0;
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    ${tw`bg-transparent shadow-xl`}
    border: none;
  }
`;

const MuteButton = styled.button`
  ${tw`absolute bottom-[1rem] left-[1rem] px-4 py-2 opacity-75 text-sm font-bold rounded hover:opacity-100 focus:outline-none focus:ring`}
  z-index: 10;
`;

const FullscreenButton = styled.button`
  ${tw`absolute bottom-[1rem] right-[1rem] text-white px-4 py-2 opacity-75 text-sm font-bold rounded hover:opacity-100 focus:outline-none focus:ring`}
  z-index: 10;
`;

const NAV_FALLBACKS = {
  nl: {
    meetTeam: "Team",
    pricing: "Prijs",
    faq: "FAQ",
    group: "Groepen",
    contact: "Contact",
    bookText: "BOEKEN",
  },
  en: {
    meetTeam: "Team",
    pricing: "Pricing",
    faq: "FAQ",
    group: "Groups",
    contact: "Contact",
    bookText: "BOOK",
  },
  de: {
    meetTeam: "Team",
    pricing: "Preise",
    faq: "FAQ",
    group: "Gruppe",
    contact: "Kontakt",
    bookText: "BUCHEN",
  },
};

const pickNavLabel = (value, fallback, englishAliases = []) => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) return fallback;
  if (
    englishAliases.length > 0 &&
    englishAliases.some((alias) => alias.toLowerCase() === trimmed.toLowerCase())
  ) {
    return fallback;
  }
  return trimmed;
};

export default () => {
  const { currentLanguage } = useLanguage();
  const fallbacks = NAV_FALLBACKS[currentLanguage] || NAV_FALLBACKS.en;
  const [content, setContent] = useState({
    ourStory: '', pricing: '', faq: '', contact: '', notification: '', heading: '', description: '', heroImage: null, bookLink: '', bookText: fallbacks.bookText, videoUrl: ''
  });
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [player, setPlayer] = useState(null); // Store YouTube player instance
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const navigate = useNavigate();


  const toggleMute = () => {
    if (player) {
      if (isMuted) {
        player.unMute();
      } else {
        player.mute();
      }
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (player) {
      const iframe = player.getIframe();
      if (iframe.requestFullscreen) {
        iframe.requestFullscreen();
      } else if (iframe.webkitRequestFullscreen) {
        iframe.webkitRequestFullscreen();
      } else if (iframe.mozRequestFullScreen) {
        iframe.mozRequestFullScreen();
      } else if (iframe.msRequestFullscreen) {
        iframe.msRequestFullscreen();
      }
    }
  };

  const onYouTubeReady = (event) => {
    setPlayer(event.target);
    event.target.mute();
  };

  const videoOptions = {
    playerVars: {
      autoplay: 1,
      mute: 1,
      loop: 1,
      playlist: content.videoUrl?.match(/(?:\?v=|\/embed\/|\.be\/|\/v\/|\/watch\?v=|&v=)?([a-zA-Z0-9_-]{11})/)?.[1]
 || "2EhO6shXVo4",
      controls: 0,
      modestbranding: 1,
      rel: 0,
      showinfo: 0,
      fs: 1,
    },
  };

  useEffect(() => {
    const langFallbacks = NAV_FALLBACKS[currentLanguage] || NAV_FALLBACKS.en;
    const replaceEnglish = currentLanguage !== "en";
    client.fetch(`*[_type == "menu" && language == "${currentLanguage}"][0]`)
      .then((data) => {
        if (!data) {
          setContent((prev) => ({ ...prev, ...langFallbacks }));
          return;
        }
        setContent({
          meetTeam: pickNavLabel(data.meetTeam, langFallbacks.meetTeam),
          pricing: pickNavLabel(
            data.pricing,
            langFallbacks.pricing,
            replaceEnglish ? ["Pricing"] : []
          ),
          faq: pickNavLabel(data.faq, langFallbacks.faq),
          group: pickNavLabel(
            data.group,
            langFallbacks.group,
            replaceEnglish ? ["Groups", "Group"] : []
          ),
          contact: pickNavLabel(
            data.contact,
            langFallbacks.contact,
            replaceEnglish ? ["Contact"] : []
          ),
          notification: data.notification,
          heading: data.heading,
          description: data.description,
          heroImage: data.heroImage,
          bookLink: data.bookLink,
          bookText: pickNavLabel(
            data.bookText,
            langFallbacks.bookText,
            replaceEnglish ? ["BOOK", "Book"] : []
          ),
          videoUrl: data.videoUrl
        });
      })
      .catch((error) => console.error('Error fetching data:', error));
  }, [currentLanguage]);

  useEffect(() => {
    const videoDelay = setTimeout(() => {
      setVideoLoaded(true);
    }, 1000);

    return () => clearTimeout(videoDelay);
  }, []);

  useEffect(() => {
    if (!player) return;
  
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          player.pauseVideo(); // Pause video when it's out of view
        } else {
          player.playVideo(); // Resume video when it comes back into view
        }
      },
      {
        threshold: 0.5, // Trigger when at least 50% of the video is visible
      }
    );
  
    const videoEl = videoRef.current;
    if (videoEl) {
      observer.observe(videoEl);
    }
  
    return () => {
      if (videoEl) {
        observer.unobserve(videoEl);
      }
    };
  }, [player]);

  useScrollToSavedSection(); // Automatically scroll to the saved section on page load
  
  const homePath = `/${currentLanguage || "nl"}`;
  const sectionHref = (id) => `${homePath}#${id}`;

  const navLinks = [
    <NavLinks key={1}>
      <NavLink href={sectionHref("crew")} onClick={(e) => { e.preventDefault(); handleNavClick("crew"); }}>
        {content.meetTeam}
      </NavLink>
      <NavLink href={sectionHref("pricing")} onClick={(e) => { e.preventDefault(); handleNavClick("pricing"); }}>
        {content.pricing}
      </NavLink>
      <NavLink href={sectionHref("faq")} onClick={(e) => { e.preventDefault(); handleNavClick("faq"); }}>
        {content.faq}
      </NavLink>
      <NavLink href={pageMappings["/groups"][currentLanguage]}>{content.group}</NavLink>
      <NavLink href={sectionHref("contact")} onClick={(e) => { e.preventDefault(); handleNavClick("contact"); }}>
        {content.contact}
      </NavLink>
      <LanguageSelector />
    </NavLinks>,
    <NavLinks key={2}>
      <PrimaryAction href={content.bookLink} target="_blank" rel="noopener noreferrer">
        {content.bookText}
      </PrimaryAction>
    </NavLinks>
  ];

  const subPages = [
    '/groups',
    '/insurance',
    '/crew',
    '/information',
    '/privacybeleid',
    '/termsAndCondition',
    '/privacyPolicy',
    '/voorwaarden',
    '/terms-condition',
    '/privacy-policy',
    '/verzekering',
    '/contact',
    '/groepsreizen',
    '/group-travel',
    '/crew2024',
    '/animator',
    '/crew-nl',
    '/informatie',
    '/crew-de',
    '/Versicherung',
    '/datenschutz',
    '/gruppe',
    '/informationen',
    '/geschaftsbedingungen'
  ];


  //const shouldDisplayVideo = subPagesInc.some(subPagesInc => window.location.pathname.includes(subPagesInc));
  const shouldDisplayVideo = !subPages.some(subPage => window.location.pathname.includes(subPage));

  return (
    <Container bgImage={content.heroImage ? urlFor(content.heroImage).url() : sl17Image}>
      <OpacityOverlay />
      <HeroContainer>
        <StyledHeader links={navLinks}>
        <LogoLink
            href={homePath}
            onClick={(e) => {
              e.preventDefault();
              localStorage.removeItem("savedSection");
              navigate(homePath);
            }}
          >
          </LogoLink>

        </StyledHeader>
      </HeroContainer>
      {videoLoaded && shouldDisplayVideo && (
        <VideoWrapper ref={videoRef}>
          <YouTube
            videoId={content.videoUrl?.match(/(?:\?v=|\/embed\/|\.be\/|\/v\/|\/watch\?v=|&v=)?([a-zA-Z0-9_-]{11})/)?.[1]
 || "JPwAsORGhQ8"}
            opts={videoOptions}
            onReady={onYouTubeReady}
          />
          <MuteButton onClick={toggleMute}>
            {isMuted ? '🔇' : '🔊'}
          </MuteButton>
          <FullscreenButton onClick={toggleFullscreen}>
            ⛶
          </FullscreenButton>
        </VideoWrapper>
      )}
    </Container>
  );
};
